/**
 * Statement actions — PDF, bundle, share, email.
 */
import { useState } from 'react'
import type { StatementDirection, StatementPreviewParams } from '../../api/statements'
import {
  createStatementMagicLink,
  exportStatementPdf,
  exportStatementBundle,
  emailStatement,
} from '../../api/statements'
import { isProdEnvironment } from '../../lib/appEnv'
import { useActionToast } from '../../lib/useActionToast'
import { actionErrorMessage } from '../../lib/actionErrorMessage'
import { track } from '../../lib/track'

interface Props {
  direction: StatementDirection
  label?: string
  params: Omit<StatementPreviewParams, 'direction'>
  ownerEmail?: string
  emailPending?: boolean
  testId?: string
  variant?: 'primary' | 'secondary'
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export default function StatementActions({
  direction,
  label = 'Statement PDF',
  params,
  ownerEmail,
  emailPending = false,
  testId,
  variant = 'secondary',
}: Props) {
  const [busy, setBusy] = useState(false)
  const { showToast, Toast } = useActionToast()

  const body: StatementPreviewParams = { direction, ...params }
  const btnClass = variant === 'primary' ? 'btn b-dk btn-sm' : 'btn b-ol btn-sm'
  const ghostClass = 'btn b-ol btn-sm'
  const placeholder = String(ownerEmail || '').trim().toLowerCase() === 'vaakapila@gmail.com'
  const sendBlocked = emailPending || !ownerEmail || placeholder

  const onExport = async () => {
    setBusy(true)
    try {
      downloadBlob(await exportStatementPdf(body), `${direction}-statement.pdf`)
      showToast('PDF downloaded')
    } catch (e) {
      showToast(actionErrorMessage(e, 'Export failed'))
    } finally {
      setBusy(false)
    }
  }

  const onBundle = async () => {
    setBusy(true)
    try {
      downloadBlob(await exportStatementBundle(body), `${direction}-statement-invoices.pdf`)
      showToast('Bundle downloaded')
    } catch (e) {
      showToast(actionErrorMessage(e, 'Bundle failed'))
    } finally {
      setBusy(false)
    }
  }

  const onMagicLink = async () => {
    setBusy(true)
    try {
      const data = await createStatementMagicLink(body)
      await navigator.clipboard.writeText(data.url)
      showToast('Share link copied')
    } catch (e) {
      showToast(actionErrorMessage(e, 'Link failed'))
    } finally {
      setBusy(false)
    }
  }

  const onEmail = async () => {
    if (sendBlocked) {
      showToast('Send is blocked until the owner billing email is set')
      return
    }
    const to = ownerEmail || window.prompt('Recipient email')?.trim()
    if (!to) return
    setBusy(true)
    try {
      const data = await emailStatement({ ...body, to, email_pending: emailPending })
      if (data.sent === false && data.reason === 'placeholder_email') {
        showToast('Send is blocked until the owner billing email is set')
        return
      }
      if (data.sent === false && data.reason === 'blocked_by_allowlist') {
        const blockedMsg = !isProdEnvironment()
          ? `Not sent — ${data.recipient ?? to} isn't on the staging allowlist`
          : `Not sent — ${data.recipient ?? to} could not receive email`
        showToast(blockedMsg)
        return
      }
      showToast('Email sent')
      track('statement_emailed', {
        project_id: body.project_id ?? undefined,
        project_count: body.project_ids?.length ?? (body.project_id ? 1 : 0),
      })
    } catch (e) {
      showToast(actionErrorMessage(e, 'Email failed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <button type="button" className={btnClass} disabled={busy} onClick={onExport} data-testid={testId || 'statement-export'}>
        {label}
      </button>
      <button type="button" className={ghostClass} disabled={busy} onClick={onBundle} data-testid="statement-bundle">
        Statement + invoices
      </button>
      <button type="button" className={ghostClass} disabled={busy} onClick={onMagicLink} data-testid="statement-magic-link">
        Copy share link
      </button>
      <button
        type="button"
        className={ghostClass}
        disabled={busy || sendBlocked}
        onClick={onEmail}
        data-testid="statement-email"
        title={sendBlocked ? 'Set the owner billing email to send' : undefined}
      >
        Send
      </button>
      {Toast}
    </div>
  )
}
